import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, ArrowRight, FileText, Upload } from 'lucide-react'
import axios from 'axios'
import SEO from '../components/SEO'
import './ProtocolScreen.css'

// Moved out of the placeholder: a placeholder shows format, not instruction,
// and it vanished the moment anyone started typing — exactly when a first-time
// user still wanted to see what a good question looks like.
const EXAMPLES = [
  'When can a patient refuse transport?',
  'What is the dose for epinephrine for a 50lb child with a severe allergic reaction?',
  'What are the contraindications for giving nitroglycerin?',
  'What is the protocol for treating chest pain?',
]

function ProtocolScreen() {
  const [currentDocument, setCurrentDocument] = useState(null)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [userInfo, setUserInfo] = useState(null)

  useEffect(() => {
    const userInfoFromStorage = JSON.parse(localStorage.getItem('userInfo'))
    setUserInfo(userInfoFromStorage)
  }, [])

  useEffect(() => {
    const fetchCurrentDocument = async () => {
      try {
        const stored = JSON.parse(localStorage.getItem('userInfo'))
        const accessToken = stored ? stored.accessToken : ''
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        const response = await axios.get(process.env.REACT_APP_GOLANGAPI + '/api/doc', config)
        if (response.data && response.data.filename) {
          setCurrentDocument({
            name: response.data.filename,
            uploadTime: response.data.uploadTime,
          })
        }
      } catch (error) {
        console.error('Error fetching current document:', error)
        setCurrentDocument(null)
      }
    }

    fetchCurrentDocument()
  }, [])

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return ''
    const date = new Date(dateTimeStr)
    return date.toLocaleString()
  }

  const handleSubmit = async () => {
    if (!question.trim()) {
      return
    }

    setSubmitting(true)
    setSubmitError('')
    setAnswer('')

    try {
      const stored = JSON.parse(localStorage.getItem('userInfo'))
      const accessToken = stored ? stored.accessToken : ''
      const userID = stored && currentDocument ? stored.id : -1
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      }

      const response = await axios.post(
        process.env.REACT_APP_GOLANGAPI + '/api/doc/question',
        {
          question,
          userID,
          filename: currentDocument ? currentDocument.name : null,
        },
        config
      )

      if (response.data && response.data.answer) {
        setAnswer(response.data.answer)
      }
    } catch (error) {
      console.error('Error submitting question:', error)
      setSubmitError(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || 'Error submitting question. Please try again.'
      )
    }

    setSubmitting(false)
  }

  const canSubmit = question.trim().length > 0 && !submitting

  return (
    <>
      <SEO
        title="PCR Protocol Questions - PCR Assistant"
        description="Ask questions about medical protocols and get AI-powered answers."
        canonicalUrl="/protocol"
      />

      <div className="pr">
        <div className="pr__wrap">
          <header className="pr__head">
            <h1 className="pr__title">Protocol questions</h1>
            <p className="pr__lede">
              Ask questions about medical protocols and get AI-powered answers.
            </p>
          </header>

          {/* ---- which document is answering ------------------------ */}
          <div className="pr__source">
            <FileText className="pr__source-icon" size={18} aria-hidden="true" />
            <div className="pr__source-body">
              <span className="pr__label">Source</span>
              {currentDocument ? (
                <>
                  <span className="pr__source-name">
                    Using your custom document: {currentDocument.name}
                  </span>
                  {currentDocument.uploadTime && (
                    <span className="pr__source-meta">
                      Uploaded: {formatDateTime(currentDocument.uploadTime)}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="pr__source-name">Using Denver Health protocol (default)</span>
                  <span className="pr__source-meta">
                    {userInfo
                      ? 'Upload your own document for organization-specific answers'
                      : 'Log in to upload your own protocol document'}
                  </span>
                </>
              )}
            </div>

            {userInfo && (
              <Link className="pr__btn" to="/documents">
                {currentDocument ? (
                  'Manage'
                ) : (
                  <>
                    <Upload size={15} aria-hidden="true" />
                    Upload document
                  </>
                )}
              </Link>
            )}
          </div>

          {/* ---- the question --------------------------------------- */}
          <div className="pr__form">
            <div className="pr__surface">
              <div className="pr__surface-bar">
                <label className="pr__label" htmlFor="pr-question">
                  Your question
                </label>
                <span className="pr__hint">Enter to send &middot; Shift + Enter for a new line</span>
              </div>
              <textarea
                id="pr-question"
                className="pr__editor"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="When can a patient refuse transport?"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    if (canSubmit) {
                      handleSubmit()
                    }
                  }
                }}
              />
            </div>

            <div className="pr__examples">
              <span className="pr__label">For example</span>
              <ul>
                {EXAMPLES.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            </div>

            {submitError && (
              <p className="pr__error" role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                <span>{submitError}</span>
              </p>
            )}

            <div className="pr__actions">
              <button
                type="button"
                className="pr__btn pr__btn--primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? 'Asking…' : 'Ask'}
                {!submitting && <ArrowRight size={16} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* ---- the answer ----------------------------------------- */}
          {(answer || submitting) && (
            <section className="pr__surface" aria-labelledby="pr-answer-label">
              <div className="pr__surface-bar">
                <span className="pr__label" id="pr-answer-label">
                  Answer
                </span>
              </div>
              <p className="pr__out" data-state={answer ? 'ready' : 'waiting'} aria-live="polite">
                {answer || 'Analyzing your question…'}
              </p>
            </section>
          )}

          <footer className="pr__foot">
            Need to upload or manage your protocol documents? Visit the{' '}
            <Link className="pr__link" to="/documents">
              Document Manager
            </Link>
            .
          </footer>
        </div>
      </div>
    </>
  )
}

export default ProtocolScreen
